'use client';

import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Link from 'next/link';
import { contactDetails, menuData } from '@/components/utils/Constant';
import Image from 'next/image';
import Button from '@/components/ui/Button';

type IMegaMenuProps = {
  setIsMegaMenuOpen: (_value: boolean) => void;
  isWorkDetailSubPages: boolean;
};

export default function MegaMenu({
  setIsMegaMenuOpen,
  isWorkDetailSubPages,
}: IMegaMenuProps) {
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<number>(0);
  const [isHovering, setIsHovering] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMenuEnter = (menuTitle: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveMenu(menuTitle);
    setActiveCategory(0);
    setIsMegaMenuOpen(true);
  };

  const handleMenuLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveMenu(null);
      setActiveCategory(0);
      setIsMegaMenuOpen(false);
    }, 150);
  };

  const handleMegaMenuEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsHovering(true);
  };

  const handleMegaMenuLeave = () => {
    setIsHovering(false);

    timeoutRef.current = setTimeout(() => {
      setActiveMenu(null);
      setActiveCategory(0);
      setIsMegaMenuOpen(false); // ✅ IMPORTANT FIX
    }, 150);
  };

  const activeMenuData = menuData.find((menu) => menu.menuTitle === activeMenu);

  const containerVariants = {
    hidden: { opacity: 0, y: -10, scale: 0.98 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.2, ease: [0.04, 0.62, 0.23, 0.98] },
    },
    exit: { opacity: 0, y: -10, scale: 0.98, transition: { duration: 0.15 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: 30 }, // start from right
    visible: (index: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: index * 0.03, // stagger effect
        duration: 0.25,
        ease: 'easeOut',
      },
    }),
  };

  return (
    <nav className="relative hidden xl:block">
      <ul className="flex items-center gap-8">
        {menuData.map((item) => (
          <li
            key={item.menuTitle}
            className="relative py-4"
            onMouseEnter={() =>
              item.megaMenuItem.length > 0 && handleMenuEnter(item.menuTitle)
            }
            onMouseLeave={() => handleMenuLeave()}
          >
            <span
              className={`${item.menuTitle === 'Optimity AI' ? 'ai-btn' : ''} cursor-pointer py-4 font-opt text-base font-medium transition-colors duration-200 ${
                activeMenu === item.menuTitle && isHovering
                  ? 'text-lightBlue'
                  : `${
                      isWorkDetailSubPages ? 'text-white' : 'text-optDesc'
                    } hover:text-lightBlue`
              }`}
            >
              {item.menuTitle}
            </span>
          </li>
        ))}
      </ul>

      <AnimatePresence>
        {activeMenu &&
          activeMenuData &&
          activeMenuData.megaMenuItem.length > 0 && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className={`absolute ${activeMenu === 'Optimity AI' ? '-left-[15%]' : 'left-0'} top-full z-50`}
              onMouseEnter={handleMegaMenuEnter}
              onMouseLeave={handleMegaMenuLeave}
            >
              <div
                className={`mt-2 flex w-full flex-col overflow-hidden rounded-b-2xl border border-subtleWhite bg-ghostWhite shadow-2xl`}
              >
                {activeMenu === 'Optimity AI' && (
                  <section className="w-full px-2 pt-3">
                    <div className="rounded-[28px] bg-[conic-gradient(from_0deg,transparent_0deg,#9daed5_285deg,rgba(203,67,32,0.75)_330deg,#3691cb_360deg)] p-[1.5px]">
                      <div className="flex flex-col justify-between gap-6 rounded-[27px] bg-white p-4 lg:flex-row">
                        <div className="flex min-w-0 flex-col gap-2">
                          <p className="text-sm font-bold text-lightBlue">
                            Optimity AI
                          </p>
                          <h2 className="text-gray-950 text-base font-bold leading-tight tracking-tight">
                            Engineering AI Solutions for Modern Businesses
                          </h2>
                          <p className="text-base leading-5 text-black/60">
                            Transform your business with intelligent AI
                            solutions, automation, and next-generation
                            technology.
                          </p>
                        </div>
                        <Button
                          btnName="Explore Optimity AI"
                          redirectionLink="/optimity-ai"
                          isBackgroung={true}
                          className="h-max text-nowrap"
                          onClick={() => handleMegaMenuLeave()}
                        />
                      </div>
                    </div>
                  </section>
                )}
                {/* Left Sidebar - Category Tabs */}
                <div className="flex w-full">
                  <div className="min-w-[120px] border-r border-subtleWhite bg-white py-3">
                    {activeMenuData.megaMenuItem.map((category, index) => (
                      <button
                        onMouseEnter={() => {
                          setActiveCategory(index);
                        }}
                        style={{
                          ['--cat-bg' as string]: category.backgroundColor,
                          ['--cat-hover-bg' as string]:
                            category.backgroundColor,
                        }}
                        key={index}
                        className={`relative w-full max-w-[350px] text-nowrap border-b border-b-subtleWhite px-6 py-3.5 text-left font-opt text-[15px] font-medium transition-all duration-200 last:border-b-0 ${
                          activeCategory === index
                            ? 'bg-[var(--cat-bg)] text-white'
                            : 'text-iconSubtle hover:bg-[var(--cat-hover-bg)]'
                        } `}
                      >
                        {category.title}
                      </button>
                    ))}
                  </div>

                  {/* Right Panel - Services Grid */}
                  <div className="min-w-[550px] flex-1 p-6">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeCategory}
                        initial={{ opacity: 0, x: 40 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -40 }}
                        transition={{ duration: 0.25, ease: 'easeIn' }}
                      >
                        <h3 className="mb-5 border-b border-b-subtleWhite pb-3 font-opt text-lg font-semibold text-primary">
                          {
                            activeMenuData.megaMenuItem[activeCategory]
                              ?.mainTitle
                          }
                        </h3>

                        <div className="grid grid-cols-2 gap-4">
                          {activeMenuData.megaMenuItem[
                            activeCategory
                          ]?.items.map((item, index) => (
                            <motion.div
                              key={item.techName}
                              custom={index}
                              variants={itemVariants}
                              initial="hidden"
                              animate="visible"
                            >
                              <Link
                                href={item.techLink}
                                onClick={() => handleMegaMenuLeave()}
                                className="group flex w-max items-center font-opt text-[15px] font-normal text-primary transition-colors duration-200 hover:text-lightBlue"
                              >
                                <span className="transition-transform duration-200 group-hover:translate-x-1">
                                  {item.techName}
                                </span>
                              </Link>
                            </motion.div>
                          ))}
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>

                <div className="border-t border-subtleWhite bg-white px-3 py-4">
                  <div className="flex items-center justify-between gap-4">
                    <Link
                      href={`tel: ${contactDetails?.cno}`}
                      className="group flex items-center gap-3"
                    >
                      <div className="flex h-[40px] w-[40px] items-center justify-center rounded-full bg-lightBlue">
                        <Image
                          src={contactDetails?.cnoi}
                          alt="call-icon"
                          width={18}
                          height={18}
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <h6 className="font-opt text-sm font-medium leading-4 text-primary">
                          {contactDetails?.callTitle}
                        </h6>
                        <span className="font-opt text-sm font-normal lowercase leading-4 text-gray transition-colors duration-200 group-hover:text-lightBlue">
                          {contactDetails?.cno}
                        </span>
                      </div>
                    </Link>
                    <Link
                      href={`mailto:${contactDetails?.hrmail}`}
                      className="group flex items-center gap-3"
                    >
                      <div className="flex h-[40px] w-[40px] items-center justify-center rounded-full bg-lightBlue">
                        <Image
                          src={contactDetails?.cmaili}
                          alt="email-icon"
                          width={18}
                          height={18}
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <h6 className="font-opt text-sm font-medium leading-4 text-primary">
                          {contactDetails?.hrmailTitle}
                        </h6>
                        <span className="font-opt text-[15px] font-normal lowercase leading-4 text-gray transition-colors duration-200 group-hover:text-lightBlue">
                          {contactDetails?.hrmail}
                        </span>
                      </div>
                    </Link>
                    <Link
                      href={`mailto:${contactDetails?.cmail}`}
                      className="group flex items-center gap-3"
                    >
                      <div className="flex h-[40px] w-[40px] items-center justify-center rounded-full bg-lightBlue">
                        <Image
                          src={contactDetails?.cmaili}
                          alt="email-icon"
                          width={18}
                          height={18}
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <h6 className="font-opt text-sm font-medium leading-4 text-primary">
                          {contactDetails?.salesmailTitle}
                        </h6>
                        <span className="font-opt text-[15px] font-normal lowercase leading-4 text-gray transition-colors duration-200 group-hover:text-lightBlue">
                          {contactDetails?.cmail}
                        </span>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
      </AnimatePresence>
    </nav>
  );
}
